import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Check, Clock, ChevronRight, ChevronLeft, FileText, Search, SlidersHorizontal, LockIcon } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../components/ui/accordion';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '../../components/ui/pagination';
import SyllabusModal from '../../components/modals/SyllabusModal';
import TestListingSort from '../../components/dropdown/TestListingSort';
import TestListingLoader from '../../components/loaders/TestListingLoader';

import { debounce } from '../../lib/debounce';

import { CheckCircleIcon, CheckIcon, ClockIcon, QuestionIcon, TargetIcon } from '@phosphor-icons/react';
import { useNavigate, useParams } from 'react-router-dom';
import { Tab, TabsContainer } from '../../utils/SubjectTabs';
import formatDateTime from './formatTime';

const testTypeOptions = [
  { id: 'All', label: 'All' },
  { id: 'Topic Test', label: 'Topic Tests' },
  { id: 'Chapter Test', label: 'Chapter Tests' },
];

function ChapterwiseTests({
  chapterWiseTests,
  chapterFilters,
  currentPage,
  totalPages,
  availableFilters,
  selectedCategoryData,
  handleChapterFilterChange,
  handleClearChapterFilters,
  handlePageChange,
  setSelectedCategory,
  setSelectedSubject,
  categories,
  totalCount,
  banners,
  loading,
  isPackPurchased,
}) {
  const navigate = useNavigate();
  const params = useParams();
  const packId = params.packId;
  const [selectedSortOption, setSelectedSortOption] = useState(null);
  const [localSearch, setLocalSearch] = useState(chapterFilters.search || '');
  const [showSyllabusModal, setShowSyllabusModal] = useState(false);
  const [syllabusModalContent, setSyllabusModalContent] = useState({ title: '', syllabus: '' });
  const [selectedTestType, setSelectedTestType] = useState('All');
  const [selectedUnit, setSelectedUnit] = useState('All');

  // Scroll mask state
  const [scrollState, setScrollState] = useState({
    canScrollLeft: false,
    canScrollRight: true,
  });
  const scrollRef = useRef(null);
  const contentRef = useRef(null); // Ref for the main content container

  useEffect(() => {
    setLocalSearch(chapterFilters.search || '');
  }, [chapterFilters.search]);

  useEffect(() => {
    handleChapterFilterChange({ sorting: selectedSortOption });
  }, [selectedSortOption]);

  useEffect(() => {
    checkScrollState();
  }, []);

  // Scroll to top when selected category changes
  // Scroll to top when selected subject/category changes
  useEffect(() => {
    if (contentRef.current) {
      // try scrolling the container
      contentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      // fallback: scroll the window
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [selectedCategoryData]);

  const checkScrollState = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setScrollState({
        canScrollLeft: scrollLeft > 0,
        canScrollRight: scrollLeft < scrollWidth - clientWidth - 1,
      });
    }
  };

  const scrollFilters = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = 200;
      const newScrollLeft =
        direction === 'left'
          ? scrollRef.current.scrollLeft - scrollAmount
          : scrollRef.current.scrollLeft + scrollAmount;

      scrollRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth',
      });
    }
  };

  const getMaskStyle = () => {
    const { canScrollLeft, canScrollRight } = scrollState;

    if (!canScrollLeft && !canScrollRight) {
      return {};
    } else if (!canScrollLeft && canScrollRight) {
      return {
        maskImage: 'linear-gradient(to right, black 90%, transparent)',
        WebkitMaskImage: 'linear-gradient(to right, black 90%, transparent)',
      };
    } else if (canScrollLeft && !canScrollRight) {
      return {
        maskImage: 'linear-gradient(to right, transparent, black 10%)',
        WebkitMaskImage: 'linear-gradient(to right, transparent, black 10%)',
      };
    } else {
      return {
        maskImage: 'linear-gradient(to right, transparent, black 10%, black 90%, transparent)',
        WebkitMaskImage: 'linear-gradient(to right, transparent, black 10%, black 90%, transparent)',
      };
    }
  };

  const debouncedFilterChange = useMemo(
    () =>
      debounce((searchValue) => {
        handleChapterFilterChange({ search: searchValue });
      }, 400),
    [handleChapterFilterChange],
  );

  const handleViewSyllabus = (title, syllabusHtml) => {
    setSyllabusModalContent({ title, syllabus: syllabusHtml });
    setShowSyllabusModal(true);
  };

  const handleSearchInput = (e) => {
    const value = e.target.value;
    setLocalSearch(value);
    debouncedFilterChange(value);
  };

  const handleSubjectChange = (subjectName) => {
    setSelectedCategory(subjectName.groupId);
    setSelectedSubject(subjectName);
  };
  const getSubjectsForCategory = () => {
    if (!selectedCategoryData) return [];
    return categories.filter((cat) => cat.parentTitle === selectedCategoryData.parentTitle);
  };

  const showUpcomingTestToast = (test) => {
    toast.custom(
      (t) => (
        <div className="bg-[var(--q3-surface-default)] border border-[var(--q3-stroke-normal)] rounded-lg p-4 shadow-lg max-w-md">
          <div className="flex items-start justify-between">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <ClockIcon className="text-[var(--q3-accent-normal)]" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-[var(--q3-accent-normal)]">
                  {test.title} will be available on {formatDateTime(test?.startDate)}
                </p>
              </div>
            </div>
            <div className="ml-4 flex-shrink-0 cursor-pointer">
              <Button
                variant={'outline'}
                className="inline-flex !bg-[var(--q3-surface-default)] shadow-none border-none text-[var(--q3-danger-normal)] hover:text-[var(--q3-danger-dark)]"
                onClick={(e) => {
                  e.stopPropagation();
                  toast.remove(t.id);
                }}
              >
                <span className="sr-only">Close</span>
                <XIcon />
              </Button>
            </div>
          </div>
        </div>
      ),
      {
        position: 'bottom-center',
        duration: 3000,
      },
    );
  };

  const EmptyChapterTests = () => (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <div className="mb-4">
        <Search className="h-16 w-16 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-[var(--q3-neutral-default)] mb-2">😕 No tests found</h3>
        <p className="text-[var(--q3-neutral-light)] mb-6">Try adjusting your search or filter criteria</p>
      </div>
      <Button
        onClick={() => {
          handleClearChapterFilters();
          setSelectedSortOption(null);
        }}
        className="rounded-full bg-[var(--q3-accent-normal)] hover:bg-[var(--q3-accent-dark)] transition-colors duration-300"
      >
        Clear Filters
      </Button>
    </div>
  );

  const handleTestNavigation = (testId, path) => {
    const url = `/${packId}/${testId}/${path}?source=test_listing&categoryId=${selectedCategoryData?.groupId || selectedCategoryData?.title}`;
    navigate(url, {
      state: { selectedCategoryId: selectedCategoryData?.groupId },
    });
  };

  return (
    <div className="md:pr-28" ref={contentRef}>
      {/* Add ref to the main container */}
      <div className="p-4 pb-3 -mx-4 md:-mx-4 lg:-mx-4 rounded-xl">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
          <h2 className="text-2xl font-bold flex flex-row items-center gap-2">
            Chapter-wise Tests{' '}
            {selectedCategoryData?.isFreemium && (
              <Badge className="text-[var(--q3-neutral-default_inverted)] bg-[var(--q3-danger-normal)] block lg:hidden">
                Free
              </Badge>
            )}
          </h2>
          <div className="relative w-full md:w-56 mt-2 md:mt-0 bg-[var(--q3-surface-glass)] border-[var(--q3-stroke-normal)] rounded-full">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search Tests"
              className="pl-10 rounded-full"
              value={localSearch}
              onChange={handleSearchInput}
            />
          </div>
        </div>
        <div className="w-full overflow-x-auto mb-4">
          <TabsContainer color={selectedCategoryData?.color} isGlass={true}>
            {getSubjectsForCategory()?.map((tab) => (
              <Tab
                key={tab._id}
                isActive={selectedCategoryData?.title === tab.title}
                color={
                  selectedCategoryData?.title === tab.title ? selectedCategoryData?.color : 'var(--q3-neutral-light)'
                }
                tabColor={tab.color}
                isGlass={true}
                onClick={() => handleSubjectChange(tab)}
              >
                <div className="flex flex-row items-center">
                  {tab.icon && <img src={tab.icon} alt={tab.title} className="" />}
                  <p className="mb-0">{tab.title}</p>
                </div>
              </Tab>
            ))}
          </TabsContainer>
        </div>

        {/* Chapter-wise Filters */}
        <div className="relative">
          {/* Left Chevron */}
          {scrollState.canScrollLeft && (
            <button
              onClick={() => scrollFilters('left')}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-[var(--q3-surface-default)] border border-[var(--q3-stroke-normal)] rounded-full p-2 shadow-sm transition-all duration-200"
            >
              <ChevronLeft className="w-4 h-4 text-[var(--q3-neutral-default)] cursor-pointer" />
            </button>
          )}

          {/* Right Chevron */}
          {scrollState.canScrollRight && (
            <button
              onClick={() => scrollFilters('right')}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-[var(--q3-surface-default)] border border-[var(--q3-stroke-normal)] rounded-full p-2 shadow-sm transition-all duration-200"
            >
              <ChevronRight className="w-4 h-4 text-[var(--q3-neutral-default)] cursor-pointer" />
            </button>
          )}

          <div
            ref={scrollRef}
            className="flex gap-2 rounded-[12px] overflow-x-auto overflow-y-hidden whitespace-nowrap relative"
            style={getMaskStyle()}
            onScroll={checkScrollState}
          >
            <div className="flex gap-2 mb-2 flex-shrink-0">
              <TestListingSort setSelectedSortOption={setSelectedSortOption} selectedSortOption={selectedSortOption} />
              <Select
                value={chapterFilters.testType || ''}
                onValueChange={(value) => {
                  handleChapterFilterChange({ testType: value === 'All' ? null : value });
                  setSelectedTestType(value);
                }}
              >
                <SelectTrigger
                  className={`rounded-full flex items-center gap-2 px-4 py-2 text-[var(--q3-neutral-default)] !bg-[var(--q3-surface-default)]  hover:bg-[var(--q3-surface-dimmest)] transition-colors w-auto cursor-pointer flex-shrink-0 shadow-none`}
                >
                  <SelectValue placeholder="Test Type" />
                </SelectTrigger>
                <SelectContent className="rounded-md border border-[var(--q3-stroke-normal)] bg-[var(--q3-surface-default)] shadow-md max-h-64 overflow-y-auto cursor-pointer">
                  {testTypeOptions.map((item) => (
                    <SelectItem key={item.id} value={item.id}>
                      {item.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {availableFilters && availableFilters.selectUnit && availableFilters.selectUnit.length > 2 && (
                <Select
                  value={chapterFilters.selectUnit || ''}
                  onValueChange={(value) => {
                    handleChapterFilterChange({ selectUnit: value === 'All' ? null : value });
                    setSelectedUnit(value);
                  }}
                >
                  <SelectTrigger
                    className={`rounded-full flex items-center gap-2 px-4 py-2 text-[var(--q3-neutral-default)] hover:bg-[var(--q3-surface-dimmest)] transition-colors w-auto cursor-pointer flex-shrink-0`}
                  >
                    <SelectValue placeholder="Select Unit" />
                  </SelectTrigger>
                  <SelectContent className="rounded-md border border-[var(--q3-stroke-normal)] bg-[var(--q3-surface-default)] shadow-md max-h-64 overflow-y-auto cursor-pointer">
                    {availableFilters.selectUnit.map((unit) => (
                      <SelectItem key={unit.unit} value={unit.unit}>
                        {unit.unit}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              <div className="border border-l-[1px] border-[var(--q3-stroke-normal)] flex-shrink-0"></div>

              {availableFilters?.testStatus?.length > 0 && (
                <>
                  {availableFilters.testStatus.map((status) => (
                    <Button
                      key={status}
                      className={`rounded-full capitalize flex-shrink-0 ${chapterFilters.testStatus === status ? 'bg-[var(--q3-accent-normal)] hover:bg-[var(--q3-accent-normal)]' : 'bg-[var(--q3-surface-glass)] hover:bg-[var(--q3-accent-light)] text-[var(--q3-neutral-default)] border border-[var(--q3-stroke-normal)]'}`}
                      onClick={() =>
                        handleChapterFilterChange({
                          testStatus: chapterFilters.testStatus === status ? null : status,
                        })
                      }
                    >
                      {status}
                      {chapterFilters.testStatus === status && <Check className="ml-2 h-3 w-3" />}
                    </Button>
                  ))}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      {banners &&
        banners.length > 0 &&
        banners
          .filter((banner) => {
            return (
              !banner.groupIds ||
              banner.groupIds?.length === 0 ||
              banner.groupIds.includes(selectedCategoryData?.groupId)
            );
          })
          .map((banner, index) => (
            <div
              key={index}
              className={`bg-[${banner.bgColor}] border-l-2 border-[${banner.borderColor}] text-[${banner.textColor}] p-4 rounded-r-lg mb-6 flex items-center justify-between`}
            >
              <div className="flex-1 text-left">{banner.content}</div>
              {banner.isButtonEnabled && (
                <div className="ml-4">
                  <Button
                    className={`bg-[${banner.buttonBgColor}] text-[${banner.buttonTextColor}] hover:bg-[${banner.buttonBgColor}]`}
                    onClick={() => window.open(banner.url, '_blank')}
                  >
                    {banner.buttonText}
                  </Button>
                </div>
              )}
            </div>
          ))}
      {loading ? (
        <TestListingLoader />
      ) : totalCount === 0 && chapterFilters.isFilterApplied ? (
        <EmptyChapterTests />
      ) : (
        <div className="space-y-4">
          {chapterWiseTests.length > 0 ? (
            <Accordion type="multiple" className="w-full space-y-4">
              {chapterWiseTests.map((subgroup, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="border rounded-lg">
                  <AccordionTrigger className="px-5 py-4 hover:no-underline group transition-colors duration-300 ease-in-out hover:bg-[var(--q3-surface-dimmer)]">
                    <div className="flex items-center gap-4 w-full">
                      <div
                        className={`flex items-center justify-center w-8 h-8 rounded-full text-[var(--q3-neutral-default)] 
                        ${
                          isPackPurchased
                            ? 'bg-[var(--q3-surface-dimmer)] group-data-[state=open]:bg-[var(--q3-accent-normal)] group-data-[state=open]:text-[var(--q3-neutral-default_inverted)]'
                            : 'bg-[var(--q3-surface-dimmer)]'
                        }`}
                      >
                        <span className="font-medium">
                          {isPackPurchased || selectedCategoryData?.isFreemium ? (
                            i + 1 + (currentPage - 1) * 10
                          ) : (
                            <LockIcon size={16} color="var(--q3-danger-normal)" />
                          )}
                        </span>
                      </div>

                      <div className="flex flex-col items-start">
                        <h3 className="font-bold text-lg">{subgroup.title}</h3>
                        <p className="text-sm text-gray-500">Total {subgroup.tests.length} tests</p>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-5 pb-4 pt-1">
                    <div className="space-y-6">
                      {subgroup.tests.map((testData, index) => {
                        const test = testData.test;
                        const isReleased = new Date() >= new Date(test.startDate);
                        let status;
                        if (!isReleased) {
                          status = 'not-released';
                        } else if (test.isTestInProgress) {
                          status = 'in-progress';
                        } else if (test.isTestAttemptedByUser) {
                          status = 'completed';
                        } else {
                          status = 'available';
                        }

                        return (
                          <Card
                            key={test._id}
                            className="border border-[var(--q3-stroke-light)] bg-[var(--q3-surface-default)] rounded-lg shadow-none py-0 overflow-hidden q3-interaction-scale-up cursor-pointer"
                            onClick={() =>
                              status === 'not-released'
                                ? showUpcomingTestToast(test)
                                : status === 'completed'
                                  ? navigate(`/${packId}/${test._id}/analysis?source=test_listing`)
                                  : navigate(`/${packId}/${test._id}/details?source=test_listing`)
                            }
                          >
                            <div className="flex flex-col w-full">
                              <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 px-2 lg:px-5 py-2 lg:py-6">
                                <div className="flex items-center w-full lg:w-fit gap-5">
                                  <div
                                    className={`hidden lg:flex items-center justify-center w-4 h-4 ${status === 'completed' ? 'bg-[var(--q3-success-normal)]' : ''} rounded-full`}
                                  >
                                    {status === 'completed' ? (
                                      <CheckIcon size={10} weight="bold" color="var(--q3-success-light)" />
                                    ) : (
                                      <span className="font-medium text-[var(--q3-neutral-light)]">
                                        {isPackPurchased || selectedCategoryData?.isFreemium ? (
                                          <>
                                            {i + 1 + (currentPage - 1) * 10}.{index + 1}
                                          </>
                                        ) : (
                                          <LockIcon size={16} color="var(--q3-danger-normal)" />
                                        )}
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex flex-col w-full lg:w-fit">
                                    <div className="flex items-start justify-start gap-2 w-full lg:w-fit text-left">
                                      <h3 className="font-bold text-md">
                                        {test.title}{' '}
                                        {test.tags &&
                                          test.tags.map((item, idx) => (
                                            <Badge
                                              key={idx}
                                              style={{
                                                backgroundColor: item.background,
                                                color: item.textColor,
                                                fontSize: '0.65rem', // smaller text
                                                padding: '2px 6px', // tighter padding
                                                borderRadius: '6px', // optional rounded corners
                                              }}
                                              className="leading-none"
                                            >
                                              {item.title}
                                            </Badge>
                                          ))}
                                      </h3>
                                    </div>

                                    <div className="flex items-center flex-wrap gap-2 mt-1 text-sm text-gray-500">
                                      <div className="flex items-center gap-1">
                                        {test.description}
                                        {test.isVideoSolutionAvailable && ` • Video solution available`}
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                <div className="flex w-full lg:w-fit flex-row gap-2">
                                  {status === 'not-released' && (
                                    <>
                                      <Button
                                        className="flex-1 rounded-full text-[var(--q3-neutral-default)] bg-[var(--q3-surface-default)] border border-[var(--q3-stroke-light)] hover:bg-[var(--q3-surface-dimmest)]"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleViewSyllabus(test.title, test.syllabus);
                                        }}
                                      >
                                        View Syllabus
                                      </Button>
                                      <Button className="flex-1 rounded-full bg-[var(--q3-accent-normal)] hover:bg-[var(--q3-accent-dark)] text-[var(--q3-neutral-default_inverted)] opacity-30 cursor-not-allowed">
                                        Attempt Test
                                      </Button>
                                    </>
                                  )}

                                  {status === 'available' && (
                                    <>
                                      <Button
                                        className="flex-1 rounded-full text-[var(--q3-neutral-default)] bg-[var(--q3-surface-default)] border border-[var(--q3-stroke-light)] hover:bg-[var(--q3-surface-dimmest)]"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleViewSyllabus(test.title, test.syllabus);
                                        }}
                                      >
                                        View Syllabus
                                      </Button>
                                      <Button
                                        className="flex-1 rounded-full bg-[var(--q3-accent-normal)] hover:bg-[var(--q3-accent-dark)] text-[var(--q3-neutral-default_inverted)]"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleTestNavigation(test._id, 'details');
                                        }}
                                      >
                                        Attempt Test
                                      </Button>
                                    </>
                                  )}

                                  {status === 'in-progress' && (
                                    <>
                                      <Button
                                        className="flex-1 rounded-full text-[var(--q3-neutral-default)] bg-[var(--q3-surface-default)] border border-[var(--q3-stroke-light)] hover:bg-[var(--q3-surface-dimmest)]"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleViewSyllabus(test.title, test.syllabus);
                                        }}
                                      >
                                        View Syllabus
                                      </Button>
                                      <Button
                                        className="flex-1 rounded-full bg-[var(--q3-yellow-normal)] hover:bg-[var(--q3-yellow-dark)] text-[var(--q3-neutral-default_inverted)]"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleTestNavigation(test._id, 'details');
                                        }}
                                      >
                                        Resume Test
                                      </Button>
                                    </>
                                  )}

                                  {status === 'completed' && (
                                    <>
                                      <Button
                                        className="flex-1 rounded-full text-[var(--q3-neutral-default)] bg-[var(--q3-surface-default)] border border-[var(--q3-stroke-light)] hover:bg-[var(--q3-surface-dimmest)]"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleTestNavigation(test._id, 'analysis');
                                        }}
                                      >
                                        View Analysis
                                      </Button>
                                      <Button
                                        className="flex-1 rounded-full bg-[var(--q3-success-normal)] hover:bg-[var(--q3-success-dark)] text-[var(--q3-neutral-default_inverted)]"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          navigate(
                                            `/${packId}/${test._id}/solutions?categoryId=${selectedCategoryData?.groupId || selectedCategoryData?.title}`,
                                            {
                                              state: { selectedCategoryId: selectedCategoryData?.groupId },
                                            },
                                          );
                                        }}
                                      >
                                        View Solution
                                      </Button>
                                    </>
                                  )}
                                </div>
                              </div>

                              <div className="px-2 lg:pl-14 lg:pr-4 flex justify-between items-center py-2 bg-[var(--q3-surface-dim)] text-[var(--q3-neutral-light)] flex-wrap">
                                {status === 'not-released' && (
                                  <div className="flex justify-between items-center w-full flex-wrap">
                                    <div className="flex items-center gap-3 flex-wrap">
                                      <div className="flex items-center gap-1 text-sm">
                                        <QuestionIcon size={20} /> <strong>{test.totalQuestions} Qs</strong>
                                      </div>
                                      <div className="flex items-center gap-1  text-sm">
                                        <ClockIcon size={20} />
                                        <strong>{test.totalTime} Min</strong>
                                      </div>
                                      <div className="flex items-center gap-1 text-sm">
                                        <CheckCircleIcon size={20} /> Score: <strong>{test.totalMarks}</strong>
                                      </div>
                                    </div>
                                    <span className="text-sm font-medium text-primary whitespace-nowrap">
                                      <Badge className="bg-[var(--q3-surface-default)] border-[var(--q3-stroke-light)] text-[var(--q3-accent-normal)]">
                                        Releasing: <strong>{formatDateTime(test.startDate)}</strong>
                                      </Badge>
                                    </span>
                                  </div>
                                )}

                                {status === 'in-progress' && (
                                  <div className="flex items-center gap-3">
                                    <div className="flex items-center gap-1 text-sm">
                                      <ClockIcon size={20} /> Time Remaining:
                                      <strong>{test.timeLeft} Min</strong>
                                    </div>
                                  </div>
                                )}
                                {status === 'completed' && (
                                  <div className="flex items-center gap-3">
                                    <div className="flex items-center gap-1 text-sm">
                                      <CheckCircleIcon size={20} /> Score:{' '}
                                      <strong>
                                        {test.totalMarkScored}/{test.totalMarks}
                                      </strong>
                                    </div>
                                    <div className="flex items-center gap-1 text-sm">
                                      <TargetIcon size={20} /> Accuracy: <strong>{test.accuracy?.toFixed(2)}%</strong>
                                    </div>
                                  </div>
                                )}
                                {status === 'available' && (
                                  <div className="flex items-center gap-3">
                                    <div className="flex items-center gap-1 text-sm">
                                      <QuestionIcon size={20} /> <strong>{test.totalQuestions} Qs</strong>
                                    </div>
                                    <div className="flex items-center gap-1 text-sm">
                                      <ClockIcon size={20} />
                                      <strong>{test.totalTime} Min</strong>
                                    </div>
                                    <div className="flex items-center gap-1 text-sm">
                                      <CheckCircleIcon size={20} /> Score: <strong>{test.totalMarks}</strong>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </Card>
                        );
                      })}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          ) : (
            <EmptyChapterTests />
          )}
        </div>
      )}
      {/* Chapter-wise Pagination */}
      {totalPages > 1 && (
        <div className="mt-6">
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#"
                  aria-disabled={currentPage === 1}
                  className={currentPage === 1 ? 'pointer-events-none opacity-50' : ''}
                  onClick={(e) => {
                    e.preventDefault();
                    if (currentPage > 1) handlePageChange(currentPage - 1);
                  }}
                />
              </PaginationItem>

              {[...Array(totalPages).keys()].map((page) => (
                <PaginationItem key={page + 1}>
                  <PaginationLink
                    href="#"
                    isActive={currentPage === page + 1}
                    onClick={(e) => {
                      e.preventDefault();
                      handlePageChange(page + 1);
                    }}
                  >
                    {page + 1}
                  </PaginationLink>
                </PaginationItem>
              ))}

              <PaginationItem>
                <PaginationNext
                  href="#"
                  aria-disabled={currentPage === totalPages}
                  className={currentPage === totalPages ? 'pointer-events-none opacity-50' : ''}
                  onClick={(e) => {
                    e.preventDefault();
                    if (currentPage < totalPages) handlePageChange(currentPage + 1);
                  }}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}
      <SyllabusModal
        open={showSyllabusModal}
        onOpenChange={setShowSyllabusModal}
        dialogContent={syllabusModalContent}
      />
    </div>
  );
}

export default ChapterwiseTests;
